/**
 * Guided product tours — a spotlight on a real element, an explanation of what
 * it is, and a Next button.
 *
 * The console tells a first-time customer how to *act* (onboarding checklist,
 * empty states) but never what a thing *is*. A tour teaches in place, pointing
 * at the actual control rather than making someone map prose onto a screen.
 *
 * Wording rules, same as `docs/user/getting-started.md` (change both together):
 *  - Say what the thing IS before what it does. A first-time user doesn't know.
 *  - No OpenStack project names (Nova, Cinder, Neutron, Octavia...). Customers
 *    never see those words anywhere else in the console. Enforced by a test.
 *  - Point at what's on screen. A step that doesn't reference its target is
 *    just a paragraph, and belongs in the docs instead.
 *
 * `target` matches a `data-tour="..."` attribute. Steps whose target isn't in
 * the DOM are skipped at runtime, so a tour still works on an empty table or
 * for a customer who can't see admin-only controls.
 */
export interface TourStep {
  /** Value of the `data-tour` attribute on the element to spotlight. */
  target: string;
  title: string;
  description: string;
}

export interface Tour {
  /** Shown in the "Take a tour" button tooltip. */
  name: string;
  steps: TourStep[];
}

/** Keyed by route pathname. */
export const TOURS: Record<string, Tour> = {
  "/": {
    name: "Getting started",
    steps: [
      {
        target: "dashboard-checklist",
        title: "Start here",
        description:
          "These steps tick themselves off as you go. Working through them leaves you with a running server you can log into.",
      },
      {
        target: "dashboard-quotas",
        title: "What you're running",
        description:
          "Live counts of what's running. Limits are generous here; if you ever hit one, this is where you'll see it.",
      },
      {
        target: "topbar-spend",
        title: "What you're spending",
        description:
          "Month-to-date charges, always visible. Charges run while a resource exists, not only while it's busy. Stopping a server ends its CPU and memory charges; its disk and public IP keep billing until you delete them.",
      },
      {
        target: "topbar-search",
        title: "Find anything",
        description: "Search every resource by name from anywhere. Ctrl+K, or Cmd+K on a Mac.",
      },
    ],
  },
  "/compute": {
    name: "Instances",
    steps: [
      {
        target: "instances-create",
        title: "An instance is a virtual machine",
        description:
          "A server that runs your software — a website, an application, anything you'd otherwise run on a physical machine. This launches one.",
      },
      {
        target: "instances-table",
        title: "Your servers live here",
        description:
          "Each row is one server. Click its name to open it, where you'll find the console, metrics, and its network settings.",
      },
      {
        target: "instances-status",
        title: "Status tells you if it's ready",
        description:
          "A new server shows Build for 30–60 seconds, then Active. This updates on its own — no need to refresh. If it fails, the instance's Events tab says why.",
      },
    ],
  },
  "/keypairs": {
    name: "SSH keys",
    steps: [
      {
        target: "keypairs-create",
        title: "An SSH key is how you log in",
        description:
          "Cloud servers have no password — you log in with an SSH key instead. You already have one from signup; create more to give separate keys to different people or machines.",
      },
      {
        target: "keypairs-table",
        title: "We keep only the public half",
        description:
          "The private key is downloadable once, at creation, and never again. Lose it and you'll need a new SSH key — servers already running keep the old one.",
      },
    ],
  },
  "/storage": {
    name: "Volumes",
    steps: [
      {
        target: "volumes-create",
        title: "A volume is a disk that outlives the server",
        description:
          "Detach it from one server and attach it to another, and your data comes with it. A server's built-in disk is erased when the server is deleted — keep anything you care about here instead.",
      },
      {
        target: "volumes-table",
        title: "Attach one to a server",
        description:
          "Create a volume, wait for Available, then use the row's actions to attach it. A freshly attached volume is blank — format and mount it once on the server before it will hold anything.",
      },
      {
        target: "volumes-kind",
        title: "Encrypted volumes and shared volumes",
        description:
          "Both are chosen when you create a volume. An encrypted volume keeps its key in your project, under Secrets, as a Volume key: it is never shown, and if it is ever destroyed the data cannot be read again. A shared volume attaches to several servers at once and needs a cluster file system — an ordinary one, mounted on two servers, will corrupt the data.",
      },
      {
        target: "volumes-tabs",
        title: "Snapshots are point-in-time copies",
        description:
          "Take one before a risky change, and roll back to it if something goes wrong. They're stored alongside your volumes. For a copy kept apart from the volume, make a backup — restoring one creates a new volume.",
      },
    ],
  },
  "/network": {
    name: "Networking",
    steps: [
      {
        target: "network-tabs",
        title: "Your private network is already set up",
        description:
          "You got one at signup and your servers are on it. You rarely need another — add one only to keep environments isolated from each other.",
      },
      {
        target: "network-tab-routers",
        title: "Routers carry your traffic out",
        description:
          "A router joins your networks to each other and to the outside. Open a router's Details to see how much data has left through it — outbound data transfer appears on your invoice, and it is not charged while we validate the counters.",
      },
      {
        target: "network-tab-floating",
        title: "Public IPs make a server reachable",
        description:
          "A server's private address only works inside your own network. Allocate a public IP here and point it at a server to reach it from the office network. It stays yours, so you can move it between servers without breaking DNS.",
      },
      {
        target: "network-tab-security",
        title: "Firewalls protect your servers",
        description:
          "Nothing can connect to your servers unless a rule allows it. SSH is already open. Add a rule whenever you open a new service — there are presets for the common ones like a website on port 80.",
      },
      {
        target: "network-tab-shared",
        title: "Share a network with another project",
        description:
          "Give another project the use of one of your networks or firewalls by entering its project ID. In the other project it appears tagged Shared by another project and is read-only there: they can put servers on it, and only you can change or delete it.",
      },
    ],
  },
  "/images": {
    name: "Images",
    steps: [
      {
        target: "images-upload",
        title: "An image is what a server boots from",
        description:
          "It's the operating-system template a new instance starts with. We provide ready-made ones; upload your own to launch servers with your software already in place.",
      },
      {
        target: "images-filters",
        title: "Find the right image",
        description:
          "Filter by name or by status. Ready means an image can boot an instance right now; Importing and Saving mean it's still being processed.",
      },
      {
        target: "images-table",
        title: "One row per image",
        description:
          "Each row shows the disk space an image needs — an instance size must offer at least that much. Row actions let you share an image with another project or download it.",
      },
    ],
  },
  "/dns": {
    name: "DNS",
    steps: [
      {
        target: "dns-tabs",
        title: "DNS points names at your servers",
        description:
          "A zone is a domain you control, like example.com. Records inside it map names to addresses, so people reach your server by name instead of by IP.",
      },
      {
        target: "dns-create",
        title: "Create a zone for your domain",
        description:
          "Enter the domain and a contact email to start managing it here. Records take effect within this cloud straight away.",
      },
      {
        target: "dns-tab-records",
        title: "Records live inside a zone",
        description:
          "Pick a zone, then open this tab to add records — an A record points a name at a public IP, a CNAME points one name at another.",
      },
    ],
  },
  "/lbaas": {
    name: "Load balancers",
    steps: [
      {
        target: "lbaas-create",
        title: "A load balancer spreads traffic",
        description:
          "It sits in front of two or more instances and shares incoming requests between them, so one busy or failed server doesn't take your service down. This provisions one; switch on Highly available to run it on two machines with automatic failover, at twice the rate. To serve HTTPS, pick the protocol that decrypts at the load balancer and choose one of your certificates; Advanced holds the security level, idle timeouts, allowed callers and sticky sessions.",
      },
      {
        target: "lbaas-table",
        title: "Your load balancers live here",
        description:
          "Click a name to manage its listeners — the ports it accepts traffic on — and the pool of instances behind it. Members can be added or removed without downtime.",
      },
      {
        target: "lbaas-filters",
        title: "Watch it come up",
        description:
          "Provisioning takes a couple of minutes; the list refreshes itself until the status settles. Type part of a name here to narrow the list as your fleet grows.",
      },
    ],
  },
  "/lbaas/:id": {
    name: "Load balancer",
    steps: [
      {
        target: "lbaas-listeners-tab",
        title: "Listeners: what it accepts, and from whom",
        description:
          "Each listener is a port the load balancer answers on. Edit one to choose its security level (Recommended accepts TLS 1.2 and 1.3, Modern 1.3 only), switch HTTP/2, set how long an idle connection is kept, or name the address ranges allowed to call.",
      },
      {
        target: "lbaas-pools-tab",
        title: "Pools: who serves, and how callers are shared out",
        description:
          "Edit a pool to switch on sticky sessions, so a returning caller reaches the member that served them before. On a member, Drain stops new connections (its weight becomes 0) while the ones in progress finish, and Backup member keeps it in reserve until no other member is healthy.",
      },
    ],
  },
  "/k8s": {
    name: "Kubernetes",
    steps: [
      {
        target: "k8s-create",
        title: "A cluster runs containers for you",
        description:
          "Kubernetes is the standard way to run containerised applications. This creates a managed cluster — you choose the size, we set up the machinery.",
      },
      {
        target: "k8s-table",
        title: "Each row is one cluster",
        description:
          "Creating takes 10-15 minutes while the nodes boot and join. Click a name for its health, node count, and the config file you need to connect with kubectl.",
      },
      {
        target: "k8s-tab-templates",
        title: "Templates define a cluster's shape",
        description:
          "A template fixes the node sizes and options a cluster is built from. Start with the provided one; add your own when you need a different shape.",
      },
    ],
  },
  "/k8s/:id": {
    name: "Kubernetes cluster",
    steps: [
      {
        target: "k8s-tab-nodegroups",
        title: "Node groups: the machines your workloads run on",
        description:
          "Each node group is a set of identical worker machines. On a cluster created to grow and shrink automatically, each group shows its minimum, maximum and how many nodes it has now; Change range moves the limits, and the same number for both keeps a group at a fixed size. Every node bills like a server while it exists.",
      },
    ],
  },
  "/database": {
    name: "Databases",
    steps: [
      {
        target: "database-create",
        title: "A managed database, without the admin work",
        description:
          "We run the database server for you — provisioning, storage and day-to-day upkeep are handled. You just connect to it from your instances.",
      },
      {
        target: "database-table",
        title: "Your databases live here",
        description:
          "A new database builds for a few minutes, then shows Running. Click its name to manage users, databases and connection details.",
      },
      {
        target: "database-tab-backups",
        title: "Backups are point-in-time copies",
        description:
          "Take one before a risky change, or restore a new database from one if something goes wrong. This tab lists them across all your databases.",
      },
    ],
  },
  "/database/:id": {
    name: "Database",
    steps: [
      {
        target: "database-tab-settings",
        title: "Tune the engine with a parameter group",
        description:
          "Settings shows the parameter group this database uses — a named set of engine settings such as connection limits, memory and timeouts. Choose one and Apply, or Remove it to go back to the defaults. Restart needed means a changed setting takes effect after a restart.",
      },
      {
        target: "database-tab-replicas",
        title: "Read replicas",
        description:
          "A read replica is a copy that follows every change, for reads only. Add one here; Promote makes it the database that accepts writes, Detach makes it a separate database. Each replica is billed as a database of its own size.",
      },
    ],
  },
  "/database/parameter-groups": {
    name: "Parameter groups",
    steps: [
      {
        target: "parameter-groups-table",
        title: "Engine settings you can reuse",
        description:
          "Each row is a parameter group: a named set of settings for one engine version. Open a row to see its settings and the databases using it. A group costs nothing.",
      },
      {
        target: "parameter-groups-used-by",
        title: "One group, many databases",
        description:
          "Changing a group changes every database that uses it. A group in use cannot be deleted — move its databases to another group or none first.",
      },
      {
        target: "parameter-groups-create",
        title: "Create a group, then attach it",
        description:
          "Pick the engine version and add only the settings you want to change; each shows what it does, its allowed range and whether it needs a restart. Then attach the group from a database's Settings tab.",
      },
    ],
  },
  "/orchestration": {
    name: "Blueprints",
    steps: [
      {
        target: "orchestration-create",
        title: "A blueprint creates many resources at once",
        description:
          "You describe servers, networks and disks in a template file, and the blueprint builds them together. Deleting the stack cleans up everything it created.",
      },
      {
        target: "orchestration-table",
        title: "Each row is one blueprint",
        description:
          "Status shows the whole group's progress — creating while resources build, complete when everything is up. If it fails, the stack's events say which resource stopped it.",
      },
    ],
  },
  "/orchestration/:id": {
    name: "Stack",
    steps: [
      {
        target: "stack-tabs",
        title: "See what this blueprint built",
        description:
          "Resources lists every piece the blueprint manages with its own status, Events is the blow-by-blow of what happened (the first place to look at a failure), and Outputs holds the values the template chose to publish — addresses, ids, connection strings.",
      },
      {
        target: "stack-update",
        title: "Preview changes before you apply them",
        description:
          "Edit the template, then Preview changes. You see which resources would be added, updated in place, replaced or deleted, and nothing is touched. Replaced means deleted and built again, so what is stored on the resource is lost. Apply stays off until you have seen the preview of exactly what you are about to apply.",
      },
      {
        target: "stack-check",
        title: "Check resources",
        description:
          "Asks each resource whether it still exists and is healthy, and shows the answer and the time on the Resources tab. It finds a server or a volume that was deleted by hand. It does not compare settings, and networks and firewalls cannot be checked.",
      },
      {
        target: "stack-tab-compare",
        title: "Compare settings",
        description:
          "Compares each server, network, port, firewall and volume with what the blueprint says, and lists every setting changed by hand: a resized volume, a renamed server, a port opened on a firewall. The next update would put those back. Nothing is changed.",
      },
    ],
  },
  "/secrets": {
    name: "Secrets",
    steps: [
      {
        target: "secrets-create",
        title: "A secret is a value kept under lock",
        description:
          "Store passwords, API tokens or certificates here instead of hard-coding them into servers and scripts. Values are encrypted at rest and revealed only when you ask.",
      },
      {
        target: "secrets-tabs",
        title: "Containers group related secrets",
        description:
          "A container bundles secrets that belong together, like a certificate with its key. Orders ask the platform to generate a new key for you rather than uploading one.",
      },
    ],
  },
  "/objects": {
    name: "Object storage",
    steps: [
      {
        target: "objects-create",
        title: "A bucket holds files, not disks",
        description:
          "Object storage keeps files — backups, media, logs — reachable over the network without attaching a disk to a server. A bucket is the folder-like container they live in.",
      },
      {
        target: "objects-table",
        title: "Your buckets live here",
        description:
          "Click a bucket to browse, upload and download its files. Storage is billed by the amount you keep, not by a fixed disk size.",
      },
      {
        target: "objects-tab-keys",
        title: "Access keys are for your code",
        description:
          "Create an access key here to read and write buckets from scripts and applications using standard S3 tools, without your console password.",
      },
    ],
  },
  "/objects/:bucket": {
    name: "Bucket settings",
    steps: [
      {
        target: "bucket-access-policy",
        title: "Who can read this bucket",
        description:
          "A bucket is private unless you say otherwise: only your project's access keys and the console reach it. An access policy can open its objects for reading; it can never let the public write.",
      },
      {
        target: "bucket-object-lock",
        title: "Object lock protects what must not change",
        description:
          "In a bucket created with object lock, a locked version cannot be deleted or overwritten until its date, and a legal hold keeps one with no end date. A lock cannot be shortened, and locked versions are billed as storage until they go.",
      },
      {
        target: "bucket-events",
        title: "Let a bucket tell you what happened",
        description:
          "An event rule publishes a message to one of your notifications whenever an object is created or removed — optionally only for names with a given beginning or ending. Everything subscribed to the notification (a queue, a function, a web address, an email) receives it. A function that writes into the bucket that triggers it starts a loop: give its output a different bucket or prefix.",
      },
    ],
  },
  "/billing": {
    name: "Billing",
    steps: [
      {
        target: "billing-spend",
        title: "What you've spent this period",
        description:
          "Charges build up while a resource exists. A stopped instance pays nothing for CPU and memory, but still bills for its disk and public IP — deleting those is what ends their charges.",
      },
      {
        target: "billing-export",
        title: "Take your usage with you",
        description:
          "Export downloads every usage row of the selected period as a CSV file — one line per resource per hour, with its cost and tags — for your own spreadsheets and cost tools. Your account can also have each day's file delivered to your object storage overnight.",
      },
      {
        target: "billing-budget",
        title: "Set a budget",
        description:
          "A budget watches what this month has cost so far, or what it is forecast to cost by month end, and notifies you when it goes over the amount. It's an alert, not a cap — nothing gets shut off.",
      },
      {
        target: "billing-breakdown",
        title: "See where the money goes",
        description:
          "Every priced resource is listed with its own running total and a month-end forecast from the last week's pace. Group by service, resource or tag to see which team or environment the money belongs to.",
      },
    ],
  },
  "/audit": {
    name: "Audit log",
    steps: [
      {
        target: "audit-table",
        title: "Every action, on the record",
        description:
          "Each row is one action taken in this account — who did it, what it touched, and when. Expand a row for the full detail of the request.",
      },
      {
        target: "audit-filters",
        title: "Narrow the trail",
        description:
          'Filter by action, resource type, or a time window to answer questions like "who deleted that server last Tuesday?".',
      },
      {
        target: "audit-export",
        title: "Take a copy",
        description:
          "Export the filtered view as a CSV file for compliance reviews or your own analysis tools.",
      },
    ],
  },
  "/functions": {
    name: "Functions",
    steps: [
      {
        target: "functions-create",
        title: "Code without servers",
        description:
          "Upload a zip of code (or point at a container image) and get back a web endpoint. You pick a memory size; the platform builds, deploys and scales it for you.",
      },
      {
        target: "functions-list",
        title: "Scales up, and down to zero",
        description:
          "Each function adds instances as traffic grows and removes them when it quiets down — all the way to zero, where an idle function costs nothing until the next request wakes it. Set Minimum instances to 1 to keep it always warm instead.",
      },
      {
        target: "functions-list",
        title: "Shared or Isolated runtime",
        description:
          "By default functions share a kernel with others on the same machine. Projects with the Isolated runtime add-on can give a function its own microVM with a private kernel: a slower first start, so it is kept warm, and memory billed at the isolated rate.",
      },
    ],
  },
  "/agent-gateways": {
    name: "Agent gateways",
    steps: [
      {
        target: "agent-gateways-create",
        title: "One endpoint for your agents",
        description:
          "An agent gateway is a single address your agents call for tools and models. It sits on one of your networks; you add the tool servers and model providers behind it, and your keys stay with the gateway instead of in every agent.",
      },
      {
        target: "agent-gateways-list",
        title: "Each gateway, at a glance",
        description:
          "Every row shows the private address on your network, the public one once this cloud has a domain, and how many tool servers, credentials and agents it carries. A gateway on this cloud's shared service is on none of your networks: its row shows the address the agents this cloud runs for you reach it at, labelled For agents running here. A number beside a name is how many calls wait for an approval. Degraded means something keeps the gateway from working as set — the gateway's Overview says what; Unreachable means the gateway stopped reporting.",
      },
    ],
  },
  "/agent-gateways/:id": {
    name: "Agent gateway",
    // The page is eight tabs shaped like the gateway (AGW-38): a step per tab first, then the
    // steps inside each — those show when their tab is the one open (absent targets are dropped).
    steps: [
      {
        target: "agent-gateway-get-started",
        title: "Get started",
        description:
          "Four steps to a working gateway — connect a model, add tools, create a credential, run an agent or connect a client — each with a check once it is done and a button that opens the right form. The card goes away when all four are done.",
      },
      {
        target: "agent-gateway-connect",
        title: "Where clients point",
        description:
          "The two addresses every client uses: one for tools (MCP) and one for models (the OpenAI-compatible API). Connect a client takes you to the line to paste for the client you use.",
      },
      {
        target: "agent-gateway-agents-address",
        title: "For agents running here",
        description:
          "On this cloud's shared service the gateway is on none of your networks. For agents running here is the address the agents this cloud runs for you reach it at; your own machines cannot, except servers you allow under Your own servers where this cloud offers it.",
      },
      {
        target: "agent-gateway-tab-tools",
        title: "Tools: what agents can do",
        description:
          "Everything an agent can call through this gateway, on one page. Add tools asks where the server comes from — the catalog, the address of a server you run, or your own image — then Discover tools lists what it offers. Below are the other agents yours may hand work to.",
      },
      {
        target: "agent-gateway-tab-models",
        title: "Models: who answers",
        description:
          "Connect a model asks who provides it and takes the API key straight from you: it is stored as a secret and the gateway adds it on the way out, so agents never see it. The names your agents ask for are set here too.",
      },
      {
        target: "agent-gateway-tab-access",
        title: "Access: who may use what",
        description:
          "One credential per agent or client, what each may use, credentials that act for a signed-in person, and the rules on what may reach a model. A new credential can be given everything, a choice of tools, or nothing yet.",
      },
      {
        target: "agent-gateway-tab-clients",
        title: "Clients: what calls the gateway",
        description:
          "A card per client — Claude Code, Claude Desktop, Cursor, VS Code, your own code — each with one thing to copy, the gateway's address already in it. Or let the gateway run your agent itself, next to it.",
      },
      {
        target: "agent-gateway-tab-playground",
        title: "Playground: try a call",
        description:
          "Run a tool, a model or an agent as one of your credentials and see what the gateway answered, how long it took, and who refused it if something did. Every playground call is on the gateway's record like any other.",
      },
      {
        target: "agent-gateway-tab-activity",
        title: "Activity: what happened",
        description:
          "One timeline first: every call, log line, security record and change on this gateway, newest at the top. The gateway's numbers, its log lines, each call as a trace, what the security plane saw where this cloud has one, and the changes people made here each keep their own view beside it.",
      },
      {
        target: "agent-gateway-tab-advanced",
        title: "Advanced: what the gateway runs",
        description:
          "Everything you set on the other tabs as the one document the gateway is given, read-only, to copy or download. Keys and credentials are never in it — only the names that stand for them.",
      },
      {
        target: "agent-gateway-playground-run",
        title: "Run as a credential",
        description:
          "The call goes through the gateway with a short-lived token for the credential you picked, so its policy and limits apply exactly as they would for an agent. The tool's form comes from what Discover saw.",
      },
      {
        target: "agent-gateway-playground-result",
        title: "What came back",
        description:
          "The answer, the request as the gateway saw it, a curl line to copy with your own credential in place, and a link to the call's trace. When an argument rule refused the call, Open this rule leads to it and the rule is shown as you saved it — the caller is told its name only.",
      },
      {
        target: "agent-gateway-rule-as-saved",
        title: "The rule as it is saved now",
        description:
          "Beside a refusal by an argument rule, the Playground shows what the rule says as it is saved now — the caller is told the rule's name only. While a change is on its way to the gateway, it may still run an earlier version of the rule; the sentence says so.",
      },
      {
        target: "agent-gateway-tab-agent-endpoints",
        title: "Let agents talk to agents",
        description:
          "Register the A2A servers your agents may call — each is reached at /agent-endpoints/<name> on this gateway, its agent card read on Discover — and publish a hosted agent so agents on this cloud reach it at /agents/<name> through the gateway. Who may call which is set per credential under Access.",
      },
      {
        target: "agent-gateway-model-aliases",
        title: "Point a model name at a provider",
        description:
          "A model alias is a name your agents ask for that you point at one of your providers, in order. Starters ask for default; move the alias to switch providers without touching any agent.",
      },
      {
        target: "agent-gateway-tab-roles",
        title: "A credential is a name and a role",
        description:
          "Viewer looks things up, Operator also makes changes, Admin may do everything, and Acts as me is a delegation. A role picks tools by what they do, so a tool found later joins the right roles, and editing a role changes every credential holding it at once.",
      },
      {
        target: "agent-gateway-tool-tiers",
        title: "What each tool does",
        description:
          "Each tool is labelled as looking things up, making changes, or deleting and overwriting — from what the tool server says, the API's method, or the catalog. A tool nobody labelled counts as making changes. Correct one here if it is wrong.",
      },
      {
        target: "agent-gateway-tab-credentials",
        title: "One credential per agent",
        description:
          "Create a credential for each agent or client and say what it may use: a role, everything on this gateway, a choice of tools, or nothing yet. It is shown once, with the lines to paste into Claude Code, an MCP client's JSON configuration or curl — the credential is already in them. Revoking one shuts that agent out within seconds.",
      },
      {
        target: "agent-gateway-tab-policy",
        title: "Decide what each credential may do",
        description:
          "Tick the tools a credential may use, grouped by server, and set requests and tokens per minute. Saving reaches the gateway within a few seconds.",
      },
      {
        target: "agent-gateway-tab-argument-rules",
        title: "Decide what a call may carry",
        description:
          "The policy says which tools a credential may call; an argument rule says what a call to one tool may carry. Pick the tool, name an argument and say when the rule matches: above an amount, outside a list, left out altogether. Then choose Refuse the call, or Ask first to let a person approve or deny it. A rule applies to every credential, and a refused call names the rule that refused it.",
      },
      {
        target: "agent-gateway-argument-rules",
        title: "Each rule, in words",
        description:
          "A rule reads as a sentence — refuse when amount is greater than 500, or ask first when it is — with a switch to keep it without applying it. Rules that refuse are checked before rules that ask first. A call that leaves the argument out is refused too, except with is given and is left out; where this cloud checks models' answers, so is a model's answer that asks for a call a rule refuses. A rule this gateway cannot apply yet reads Saved, not in force, with the reason, and changes nothing until it is in force.",
      },
      {
        target: "agent-gateway-hold-choice",
        title: "Refuse, or ask first",
        description:
          "When the rule matches, Refuse the call tells the caller at once which rule refused it; Ask first keeps the call waiting until someone approves or denies it under Activity → Approvals, and nobody answering in time refuses it. A gateway that cannot ask first yet saves the rule and says so: until it can, the calls the rule names go through without anyone being asked.",
      },
      {
        target: "agent-gateway-tab-session-rules",
        title: "Rules that depend on what came before",
        description:
          "A session rule refuses tools once a session has touched a kind of data — no mail to the outside after customer data was read, nothing destructive after a web page was fetched. A session is everything one credential does, until an hour passes without a call or 24 hours in all.",
      },
      {
        target: "agent-gateway-session-rules",
        title: "Kinds of data, then rules",
        description:
          "First say what a kind of data is: the tools whose results are it, or the patterns that give it away in any tool result. Then add a rule: once a session has touched that kind, refuse the tools that make changes, the destructive ones, or the tools you name. A refused call reports the rule, the kind of data and the tool that brought it in — never the data. A rule this gateway cannot apply yet reads Saved, not in force.",
      },
      {
        target: "agent-gateway-tab-delegations",
        title: "Let an agent act as you",
        description:
          "Mark a tool server as one that sees the user, then create a delegated credential: every call that agent makes to that server carries your identity, never the credential. It stops the moment you sign out of the session you created it in.",
      },
      {
        target: "agent-gateway-tab-content-rules",
        title: "Decide what may reach a model",
        description:
          "Content rules are applied by the gateway to model calls: pattern sets such as card numbers or cloud keys, your own patterns, or a webhook you run — a match is masked before the model sees it or the call is refused. Nothing about a call is kept; a refused call is one log line naming the rule kind.",
      },
      {
        target: "agent-gateway-content-rules",
        title: "Rules run in order",
        description:
          "Each rule names what it looks for, what it does with a match and whether it reads the request, the response or both. They run top first, so a mask placed before a webhook means the webhook sees the masked text; switch a rule off to keep it without applying it.",
      },
      {
        target: "agent-gateway-connect-credential",
        title: "A credential without the pasting",
        description:
          "Name a new credential here and every line below fills itself in with it. It is shown this once: copy the line you need before you leave the tab.",
      },
      {
        target: "agent-gateway-snippet",
        title: "One thing to copy per client",
        description:
          "Each card says where its text goes. Claude Code takes one terminal line; Claude Desktop, Cursor and VS Code take a block in their configuration file; your own code takes the two addresses and the credential as a bearer.",
      },
      {
        target: "agent-gateway-trust-ca",
        title: "Encrypted on your network",
        description:
          "On your network the lines use the gateway's encrypted address. It is signed by this cloud's own certificate authority: download the file once and keep it where the lines run — they name it — or add it to the machine's trusted certificates.",
      },
      {
        target: "agent-gateway-encryption",
        title: "The gateway's certificate",
        description:
          "Each gateway has its own certificate for its address on your network, renewed automatically before it ends. The unencrypted ports stay open for one release so you can move your clients across.",
      },
      {
        target: "agent-gateway-tab-agents",
        title: "Run agents next to the gateway",
        description:
          "Give a container image, a size and a credential, and the gateway runs the agent for you. It can reach the gateway and nothing else; start, stop, pause and read its output from here. On a gateway on this cloud's shared service, where this cloud offers it, Your own servers below lists the servers of yours allowed to call the gateway.",
      },
      {
        target: "agent-gateway-own-servers",
        title: "Your own servers",
        description:
          "Where this cloud offers Your own servers, allow a server of this project to call the gateway and name the credentials it may call with — up to 8, none of them an agent's. A server with no credential named is refused. Isolate cuts one off at once and keeps it listed; Remove takes its access away. If the list cannot be read, the section says so with Retry.",
      },
      {
        target: "agent-gateway-add-agent",
        title: "Start from a starter",
        description:
          "Add agent offers the starters proven on this cloud: pick one and the image, the size and the environment it needs are filled in, with what it needs registered first and its caveats shown before you save. Or bring your own image.",
      },
      {
        target: "agent-gateway-add-agent",
        title: "Build from a zip",
        description:
          "Or upload a zip of your own code: pick a runtime (a Python agent on the plain starter, plain Python, or Node.js), drop the zip, and the gateway builds it into an image and runs it. The build's state and its log are on the agent's row; a failed build never replaces a running agent.",
      },
      {
        target: "agent-gateway-tab-budgets",
        title: "Cap a day or a month",
        description:
          "A budget limits requests or tokens per day, tokens or cost per month, for the whole gateway or one credential. The product counts every few minutes; when a limit is used up the gateway refuses that credential's calls until the window turns, and raising the limit lets them through at once.",
      },
      {
        target: "agent-gateway-add-budget",
        title: "Set a budget",
        description:
          "Pick the whole gateway or one credential and fill in only the limits you want. A bar shows what was spent against each; to be told before one runs out, alarm on the audit event agent_gateway.budget.* with a webhook.",
      },
      {
        target: "agent-gateway-tab-apps",
        title: "Let AI apps sign in",
        description:
          "Connected apps lets claude.ai, Claude Desktop and other apps that support MCP sign-in reach this gateway's tools with no key to paste: the person adds the gateway's address in the app, signs in to this console and picks what the app may use.",
      },
      {
        target: "agent-gateway-connected-apps",
        title: "Every approval, revocable",
        description:
          "Each approval is listed with who gave it, the role it carries and when it was last used. Revoking one refuses the app from the gateway's next update; removing an app revokes all of its approvals. It needs the console on a public HTTPS address, and a gateway whose tool servers see the user uses delegated credentials instead.",
      },
      {
        target: "agent-gateway-tab-timeline",
        title: "One timeline",
        description:
          "What happened on this gateway in one list: the calls it handled, what it and its agents wrote, what the security plane recorded and what people changed here, a playground run included. A call and its log line are one row; where the gateway's record says which of your rules refused or held a call, the row names it, with Open this rule for an argument rule. Filter by kind, outcome, credential, agent or tool; Trace opens the call step by step, Details opens that kind's own view. If part of the record could not be read, the list says which part and why.",
      },
      {
        target: "agent-gateway-tab-approvals",
        title: "Approvals: answer a call that waits",
        description:
          "A rule whose action is Ask first keeps a tool call waiting instead of refusing it. It is listed here with the tool, the rule that asked, who called and the time left; Approve lets that call go ahead (unless another call in the same request is denied, which refuses the request), Deny refuses it and asks you why, and nobody answering in time refuses it too. You see which tool and which rule, not the values in the call. A number on this tab, on Activity and on the gateway list says something is waiting. A call still waiting when the gateway is updated or restarted ends without running.",
      },
      {
        target: "agent-gateway-waiting-time",
        title: "How long a call waits",
        description:
          "The waiting time is how long a call waits for an answer before the gateway refuses it by itself; the box says the range it takes and keeps what you typed. Up to 64 calls can wait at once; one more is refused. From gateway software version 22 a call that is waiting keeps waiting through a change of the waiting time.",
      },
      {
        target: "agent-gateway-flow",
        title: "The flow: where every call went",
        description:
          "Every call in the window, left to right: who made it, the gateway that decided it, the check that stopped it or let it through — the route, the credential, its policy, its limits, in that order — and where it went. Grey is still on its way; colour is how it ended. Click a caller or a check to see those calls in the Timeline. The last hour refreshes itself; OmniCloud's own checks are left out unless you switch them on.",
      },
      {
        target: "agent-gateway-reach",
        title: "What each credential can reach",
        description:
          "What the gateway enforces, drawn the same way: each active credential, its role, the tool servers, models and agents it may call, and what those tools do. It changes the moment a role, a policy or a budget does.",
      },
      {
        target: "agent-gateway-config",
        title: "See a change before it lands",
        description:
          "While a change is on its way the tab shows what differs from the configuration the gateway still runs; once the gateway reports the new one, it says since when. A change the gateway refuses stays shown as on its way, with the reason on the Overview.",
      },
      {
        target: "agent-gateway-tab-metrics",
        title: "See what went through",
        description:
          "Requests served and refused, tokens in and out, calls per tool server and each agent's uptime, minute by minute for up to a week. Refused calls are the policy doing its job: a tool not allowed, a bad credential or a usage limit.",
      },
      {
        target: "agent-gateway-tab-logs",
        title: "Read the lines",
        description:
          "Every call the gateway handled, with who made it and why it was refused, next to each agent's own output. Filter by source or agent, search a word, follow live, or download the window as CSV. Only your project's lines are ever shown.",
      },
      {
        target: "agent-gateway-tab-traces",
        title: "Follow one call",
        description:
          "Each call as a trace: who made it, which agent, the tool or model it reached, how long every step took and the tokens it used. Filter by credential, agent, tool or model, open a trace from its line in the logs, or download the window as CSV.",
      },
      {
        target: "agent-gateway-tab-security",
        title: "What the security plane saw",
        description:
          "On a cloud with the security plane, every connection this gateway and its agents make crosses it. This view shows what it denied and why, a tool that returned far more than usual, and any read of the metadata address, in one timeline with the gateway's own lines. Isolate stops an agent and has the plane permit it nothing until you release it.",
      },
    ],
  },
  "/queues": {
    name: "Queues",
    steps: [
      {
        target: "queues-create",
        title: "A buffer between your services",
        description:
          "A queue holds messages until a consumer is ready — the sender and receiver never need to be online at the same time, and a traffic spike waits in line instead of overwhelming anything.",
      },
      {
        target: "queues-list",
        title: "Watch the depth",
        description:
          "Available counts messages ready to be received; in flight are ones a consumer holds right now. A steadily growing Available column means consumers are not keeping up.",
      },
    ],
  },
  "/topics": {
    name: "Notifications",
    steps: [
      {
        target: "topics-create",
        title: "Publish once, deliver everywhere",
        description:
          "A notification broadcasts each published message to all of its subscriptions — queues in your project, your functions, signed webhooks, or email. Publishers never need to know who is listening.",
      },
      {
        target: "topics-list",
        title: "Watch your deliveries",
        description:
          "Pending deliveries counts notifications still on their way. Failing endpoints are retried with backoff, and one that stays down for days is switched off automatically so you can fix and re-enable it.",
      },
    ],
  },
  "/machines": {
    name: "Machines",
    steps: [
      {
        target: "machines-list",
        title: "Every machine, and whether it is well",
        description:
          "Each row is one physical machine your cloud runs on. The health column gives you a verdict and the single most important thing about it — you should not need to read a graph to know whether something needs doing.",
      },
      {
        target: "machines-list",
        title: "Open one to see what to do",
        description:
          "Selecting a machine explains every check in plain words: what is wrong, why it matters to your customers, and the exact step to fix it. Anything that needs a command gives you one you can copy.",
      },
      {
        target: "machines-tabs",
        title: "Health asks a different question from Capacity",
        description:
          "Health tells you whether anything is wrong. Capacity tells you whether there is room — how full each machine's processor, memory and disk are, and how much is left across all of them together. A machine can be perfectly healthy and completely full.",
      },
      {
        target: "machines-failover",
        title: "Failover: which machines cover for each other",
        description:
          "Group machines into failover segments. An instance a customer has marked highly available is restarted automatically when it crashes — and on another machine in the same segment when its machine fails. A machine outside every segment is not covered.",
      },
    ],
  },
  "/maintenance": {
    name: "Maintenance",
    steps: [
      {
        target: "maintenance-hosts",
        title: "Every compute host, and what is on it",
        description:
          "Each row is one physical machine your customers' instances run on. The utilisation bars tell you whether the rest of the fleet has room to take this one's workload before you close it.",
      },
      {
        target: "maintenance-tabs",
        title: "Close it, empty it, take it down",
        description:
          "Closing a host stops new instances landing on it — the ones already running are untouched. Plan drain then works out where they should go and shows you the moves before any of them happen.",
      },
      {
        target: "maintenance-instances",
        title: "Move a single instance yourself",
        description:
          "Every customer instance on the chosen host, with the customer it belongs to. A live move keeps the instance running and its owner never notices; a stopped instance has to move cold, which restarts it on the new host.",
      },
      {
        target: "maintenance-migrations",
        title: "Watch the moves land",
        description:
          "Moves in progress, wherever they were started from. A cold move waits in \u201cAwaiting confirmation\u201d until you accept it — only then is the copy on the old host freed.",
      },
    ],
  },
  "/optimization": {
    name: "Optimization",
    steps: [
      {
        target: "optimization-aims",
        title: "Start from what you want to improve",
        description:
          "Pick an outcome — using fewer machines, evening out the load, using less power. The platform looks at how your customers' instances are spread across your machines and writes up what it would change.",
      },
      {
        target: "optimization-tabs",
        title: "Read it before anything moves",
        description:
          "Every recommendation is written in plain words: which instances would move, whether customers would notice, and any step that might not work. Nothing happens until you choose to apply it.",
      },
      {
        target: "optimization-run-audit",
        title: "Or set up your own check",
        description:
          "Checks run gives you the full control: choose exactly which analysis to run, or schedule one to repeat.",
      },
    ],
  },
  "/monitoring": {
    name: "Monitoring",
    steps: [
      {
        target: "monitoring-utilisation",
        title: "Cluster capacity at a glance",
        description:
          "These gauges show how much of the platform's CPU, memory and disk is already committed. New instances stop scheduling once memory runs out.",
      },
      {
        target: "monitoring-hypervisors",
        title: "The physical machines underneath",
        description:
          "Each row is one physical host and its share of the load. A host near its memory ceiling can't take more instances even if others sit idle.",
      },
    ],
  },
  "/logs": {
    name: "Logs",
    steps: [
      {
        target: "logs-filters",
        title: "Every service's log lines, in one place",
        description:
          "Type a word from the message, or narrow by machine, program and severity. The window defaults to the last 15 minutes; widen it when you are chasing something older.",
      },
      {
        target: "logs-table",
        title: "Newest first, and it can follow along",
        description:
          "Lines arrive newest at the top. Switch on Follow to refresh every five seconds while you reproduce a problem, and open a row to read the full message and which logger wrote it.",
      },
    ],
  },
  "/credentials": {
    name: "Credentials",
    steps: [
      {
        target: "credentials-tabs",
        title: "Keys for code, not people",
        description:
          "These credentials let scripts and applications call the platform without your password. Each tab is a different kind: storage keys, API keys, and application credentials.",
      },
      {
        target: "credentials-create-app",
        title: "An application credential acts as you",
        description:
          "It carries your permissions but has its own secret, which can expire or be revoked on its own. Ideal for CI jobs and services that deploy on your behalf.",
      },
      {
        target: "cli-signin-download",
        title: "Sign in from the command line",
        description:
          "The openstack command-line tool, the SDKs and Terraform can use the same sign-in as the console. Download this file, put it in ~/.config/openstack/clouds.yaml, and your console password signs you in.",
      },
      {
        target: "cli-signin-yaml",
        title: "What the file holds",
        description:
          "Where to sign in, which account and project, and the region — never your password. Copy it into a tool that reads clouds.yaml, or read the values below it to wire Terraform by hand.",
      },
    ],
  },
  "/alarms": {
    name: "Alarms",
    steps: [
      {
        target: "alarms-create",
        title: "Get told before your customers do",
        description:
          "An alarm watches one metric on one resource — CPU, memory, disk, network, queue depth or an audit event — and flips to In alarm once it passes your threshold.",
      },
      {
        target: "alarms-table",
        title: "State, not noise",
        description:
          "Each alarm is OK, In alarm, or Insufficient data (not enough readings yet). Give it up to five actions — call a web address, publish to a topic, stop or restart a server, or add a server to or remove one from an auto scaling group — and open an alarm to see every flip, and any action that failed, in its History.",
      },
    ],
  },
  "/account": {
    name: "Account",
    steps: [
      {
        target: "account-security",
        title: "Lock down your sign-in",
        description:
          "Turn on two-factor authentication and keep the backup codes somewhere safe — they are the way back in if you lose your phone.",
      },
      {
        target: "account-sessions",
        title: "Every device you're signed in on",
        description:
          "Revoke anything you don't recognise. Signing out everywhere else is one click, and doesn't disturb this session.",
      },
      {
        target: "account-tours",
        title: "Replay any tour",
        description:
          "Reset the tours here, or take the current page's tour any time from the ? in the header.",
      },
    ],
  },
  "/queues/:id": {
    name: "Queue",
    steps: [
      {
        target: "queues-stats",
        title: "What's in the queue right now",
        description:
          "Visible messages are ready to be received; in-flight ones have been handed to a consumer that hasn't acknowledged them yet. In-flight messages come back automatically if the consumer never finishes.",
      },
      {
        target: "queues-send",
        title: "Send a test message",
        description:
          "Put a message on the queue from here to check your setup without writing any code first.",
      },
      {
        target: "queues-receive",
        title: "Receive and acknowledge",
        description:
          "Receiving hides a message from other consumers while you work on it. Acknowledge to delete it for good — do nothing and it returns to the queue for another attempt.",
      },
      {
        target: "messaging-grants",
        title: "Share it with another project",
        description:
          "Grant another project permission to send or receive here without handing over your credentials. Grants disappear if this queue is deleted, and you keep paying for the requests.",
      },
    ],
  },
  "/topics/:id": {
    name: "Notification",
    steps: [
      {
        target: "topics-subscriptions",
        title: "Subscribers decide where messages go",
        description:
          "One published message is delivered to every subscription — a queue, a function, a webhook or an email address. Add a filter to send a subscriber only the messages it cares about.",
      },
      {
        target: "topics-publish",
        title: "Publish a test message",
        description:
          "Publish from here and watch it fan out. The Deliveries tab shows each attempt, so a failing subscriber is easy to spot.",
      },
    ],
  },
  "/vpn": {
    name: "VPN",
    steps: [
      {
        target: "vpn-table",
        title: "A VPN connection links two sites",
        description:
          "Each row is an encrypted tunnel between one of your private networks here and a network at another site — an office, a data centre, another cloud. Peer address is the gateway at the far end.",
      },
      {
        target: "vpn-status",
        title: "Status tells you if the tunnel is up",
        description:
          "Connected means traffic is flowing. Tunnel down usually means the settings on the two sides differ — open the connection and compare its Peer setup tab with what the other side entered.",
      },
      {
        target: "vpn-create",
        title: "Create a connection",
        description:
          "Pick a router with an external gateway, the local networks to share, and the other site's address and networks. You'll enter a pre-shared key both sides must know.",
      },
    ],
  },
  "/vpn/:id": {
    name: "VPN connection",
    steps: [
      {
        target: "vpn-peer-setup",
        title: "What the other side needs",
        description:
          "This list is written for whoever manages the far-end gateway: their remote gateway is this cloud's address, their remote networks are yours. Copy it and send it to them.",
      },
    ],
  },
  "/compute/autoscaling-groups": {
    name: "Auto scaling groups",
    steps: [
      {
        target: "autoscaling-table",
        title: "A group is a set of identical servers",
        description:
          "Each row is one group: servers launched from the same image, size and network, that the group adds to when their average CPU — or memory in use — is high and removes from when it is low. Open a group to see its members, its history and its schedules.",
      },
      {
        target: "autoscaling-size",
        title: "Size is members now, and the range they stay in",
        description:
          '"2 of 1–3" means two servers are running and the group will never go below one or above three. Change the minimum and maximum from the group\'s Settings tab; the group does the rest.',
      },
      {
        target: "autoscaling-create",
        title: "Create a group",
        description:
          "Pick what every member looks like — image, size, network, firewalls, SSH key — then the minimum, maximum, whether the group scales on CPU or on memory, and the levels at which it grows and shrinks. For memory the level is a share of the instance size's memory. Members bill like ordinary servers.",
      },
    ],
  },
  "/compute/scheduled-actions": {
    name: "Scheduled actions",
    steps: [
      {
        target: "schedules-table",
        title: "A schedule does one thing at set times",
        description:
          "Each row is one schedule: stop, start or reboot a server, snapshot a volume, or set the size of an auto scaling group, at the times you chose. Times are UTC. Open a schedule to see every run and whether it succeeded.",
      },
      {
        target: "schedules-enabled",
        title: "Pause a schedule without losing it",
        description:
          "Switch a schedule off and it keeps its settings but never runs; switch it back on and the next run is planned again.",
      },
      {
        target: "schedules-create",
        title: "Create a schedule",
        description:
          "Pick a server, a volume or an auto scaling group, then a preset — nightly stop, nightly start, weekly snapshot — or Custom for your own five cron fields. For a group, give the minimum, the maximum and how many members to run. For a volume, say how many snapshots to keep, or for how many days, and the schedule deletes the older ones it made. Each target can carry up to five schedules.",
      },
    ],
  },
  "/compute/placement-groups": {
    name: "Placement groups",
    steps: [
      {
        target: "placement-groups-table",
        title: "A placement group decides which machine a server lands on",
        description:
          "Each row is one group and the servers launched into it. Open a row to see them. A group costs nothing; its servers bill like any other server.",
      },
      {
        target: "placement-groups-rule",
        title: "Spread apart, or keep together",
        description:
          "Spread apart puts the group's servers on different machines, so one machine failing does not take them all. Keep together puts them on the same machine, so traffic between them is as fast as it can be. Strict turns the rule into a requirement: a launch that cannot follow it is refused and nothing is created.",
      },
      {
        target: "placement-groups-create",
        title: "Create a group, then launch into it",
        description:
          "Name the group and pick its rule — the rule cannot be changed later. Then choose the group under Zone when you launch an instance; a server joins a group only at launch.",
      },
    ],
  },
  "/compute/launch-templates": {
    name: "Launch templates",
    steps: [
      {
        target: "launch-templates-table",
        title: "A launch template saves a server's settings under a name",
        description:
          "Each row is one template: operating system, instance size, networks, SSH key, firewalls and user data. Launch opens the launch wizard filled in from it; you can still change anything before you launch. A template costs nothing.",
      },
      {
        target: "launch-templates-create",
        title: "Create a template, or save one from the launch wizard",
        description:
          "Create one here, or use Save as template on the launch wizard's Review step. Editing or deleting a template does not change servers already launched from it.",
      },
      {
        target: "launch-templates-versions",
        title: "Keep versions, roll forward or back",
        description:
          "Edit a template with Save as a new version to keep the old settings. Versions lists them all: launch any one, make one the default (what Launch uses), or delete one you no longer need.",
      },
    ],
  },
  "/compute/autoscaling-groups/:id": {
    name: "Auto scaling group",
    steps: [
      {
        target: "autoscaling-tab-queue-scaling",
        title: "Grow the group when a queue backs up",
        description:
          "Pick one of your queues and two numbers. While more messages wait than the first, a server is added every 5 minutes; while fewer wait than the second, one is removed. The group never goes outside its minimum and maximum, and CPU or memory scaling keeps working beside it.",
      },
    ],
  },
  "/network-acls": {
    name: "Network ACLs",
    steps: [
      {
        target: "acls-table",
        title: "A Network ACL guards a whole router",
        description:
          "Each row is an ordered list of allow and deny rules attached to one of your routers, so it applies to every server behind that router at once. Firewalls protect individual servers; a Network ACL protects the network edge.",
      },
      {
        target: "acls-status",
        title: "Status tells you if the rules are being enforced",
        description:
          "Active means the rules are in force on the router. Not attached means the ACL exists but guards nothing yet — open it and attach a router.",
      },
      {
        target: "acls-create",
        title: "Create a Network ACL",
        description:
          "Give it a name. It starts with no rules, and once attached to a router it denies every new connection through that router, in and out, until a rule allows it — so for a router that is in use, add the rules from the ACL's Rules tab first and attach it afterwards.",
      },
    ],
  },
  "/network-acls/:id": {
    name: "Network ACL",
    steps: [
      {
        target: "acls-router",
        title: "Attach it to a router",
        description:
          "The rules take effect on every network behind the router you choose here. A router can carry one Network ACL at a time; detach it and the router goes back to letting everything through.",
      },
      {
        target: "acls-rules",
        title: "Rules are checked top to bottom",
        description:
          "The first rule that matches a connection decides whether it is allowed or denied, and later rules are ignored. Use the arrows to move a rule up or down; anything no rule matches is denied, in either direction.",
      },
      {
        target: "acls-add-rule",
        title: "Add a rule",
        description:
          "Pick allow or deny, the protocol, and the source and destination address ranges and ports. Leave a field empty to match anything — a deny rule with everything empty blocks all traffic.",
      },
    ],
  },
  "/flow-logs": {
    name: "Flow logs",
    steps: [
      {
        target: "flow-logs-table",
        title: "A flow log records which connections were allowed or dropped",
        description:
          "Each row watches one Firewall or one Network ACL and writes a line for every connection it accepts, drops, or both. Use it to see who is knocking and to check that a new rule blocks what you meant it to.",
      },
      {
        target: "flow-logs-create",
        title: "Create a flow log",
        description:
          "Choose what to watch — a Firewall on your servers or a Network ACL on a router — and which events to keep. Dropped only is the usual choice: it shows the blocked attempts without flooding the log with normal traffic.",
      },
      {
        target: "flow-logs-records",
        title: "Read the records",
        description:
          "Pick a flow log and a time window to see its records: when, from which address, to which port, and whether the connection was accepted or dropped. New lines appear within about a minute.",
      },
    ],
  },
  "/traffic-mirrors": {
    name: "Traffic mirroring",
    steps: [
      {
        target: "mirrors-table",
        title: "A mirror copies a server's traffic to a monitor server",
        description:
          "Each row sends a copy of the network traffic of one or more source servers to a monitor server you own — for packet capture, intrusion detection or debugging. The original traffic is not changed or slowed. Expand a row to see its sources.",
      },
      {
        target: "mirrors-status",
        title: "Status tells you if the copies are flowing",
        description:
          "Active means the monitor server is receiving copies. Down means the monitor or a source is not attached to a running server — start the server, or delete the mirror and create it again.",
      },
      {
        target: "mirrors-create",
        title: "Create a mirror",
        description:
          "Choose the monitor server and one of its ports, then add up to eight source servers with a direction each: incoming, outgoing, or both. A project can have three mirrors; the monitor must be one of your own servers.",
      },
    ],
  },
  "/shares": {
    name: "File shares",
    steps: [
      {
        target: "shares-table",
        title: "A file share is a folder several servers use at once",
        description:
          "Each row is a network file system on one of your private networks. Any server on that network can mount it over NFS, so several servers read and write the same files.",
      },
      {
        target: "shares-status",
        title: "Status tells you when it can be mounted",
        description:
          "Creating means the file server is still being set up — usually a minute or two. Available means the mount command is ready; open the share to copy it.",
      },
      {
        target: "shares-create",
        title: "Create a share",
        description:
          "Pick a name, the network the share should be reachable from, and a size in GB. You can grow a share later but never shrink it.",
      },
    ],
  },
  "/shares/:id": {
    name: "File share",
    steps: [
      {
        target: "shares-mount",
        title: "Mount it from a server",
        description:
          "Copy this command and run it on any server on the share's network. Before that, add the server's address on the Access tab — the file server refuses everything else.",
      },
    ],
  },
  // Content delivery (plan CDN-4): source copy is docs/user/getting-started.md "Content delivery".
  "/content-delivery": {
    name: "Content delivery",
    steps: [
      {
        target: "content-delivery-table",
        title: "A distribution serves your content through a cache",
        description:
          "Each row is a distribution: a hostname of its own in front of an origin — one of your buckets, load balancers or servers, or a site elsewhere. It keeps copies of what the origin sends, so repeat visitors are answered without asking the origin again. The Delivered from column says where it answers from.",
      },
      {
        target: "content-delivery-status",
        title: "Status tells you when it is serving",
        description:
          "Creating and Updating mean a change is still being applied; the list refreshes itself until it settles. Active means the hostname is answering. Origin unreachable means the origin has stopped answering — visitors still get what is cached.",
      },
      {
        target: "content-delivery-create",
        title: "Create a distribution",
        description:
          "Pick the origin, then a cache policy: Static keeps everything for a day, Dynamic keeps nothing, Custom lets you decide path by path. The last step estimates the cost for a month of traffic you enter, before you commit.",
      },
    ],
  },
  "/content-delivery/:id": {
    name: "Distribution",
    steps: [
      {
        target: "content-delivery-hostnames",
        title: "Point your visitors here",
        description:
          "This is the distribution's hostname. Use it wherever you would have used the origin's address — in links, in your application's settings, or as the target of a DNS record.",
      },
      {
        target: "content-delivery-location",
        title: "Where it is delivered from",
        description:
          "Content is delivered from this one location. Visitors far from it still reach it; they are just further away than visitors nearby.",
      },
      {
        target: "content-delivery-domains-tab",
        title: "Domains & certificates: a hostname of your own",
        description:
          "Serve the distribution on a hostname you own, such as assets.example.com. Each one has a certificate: issued automatically, or one of your own from Secrets.",
      },
      {
        target: "content-delivery-default-hostname",
        title: "The default hostname",
        description:
          "Every distribution has this hostname. Published means it answers from the internet; if it is not published yet, the reason is shown beside it. A custom domain is a DNS record of yours that points here.",
      },
      {
        target: "content-delivery-add-domain",
        title: "Add a domain",
        description:
          "Enter the hostname and choose its certificate. You are then shown the one DNS record to create at your DNS provider. The domain turns Active once that record is seen and the certificate is ready; Check again looks straight away.",
      },
      {
        target: "content-delivery-behaviours-tab",
        title: "Cache behaviours: what is kept, and for how long",
        description:
          "A cache behaviour covers a path — a folder, a file type or one exact file — and says how long a copy is kept and whether the query string, cookies or chosen headers are passed to the origin. Adding one switches the distribution to a custom cache policy.",
      },
      {
        target: "content-delivery-invalidations-tab",
        title: "Invalidations: publish a change now",
        description:
          "After you change something at the origin, create an invalidation for its path so visitors get the new version straight away instead of when the cached copy expires. End a path with * to cover everything below it.",
      },
      {
        target: "content-delivery-metrics-tab",
        title: "Metrics: what visitors asked for",
        description:
          "Requests, data sent, the share answered from the cache and responses by status class, over the last hour up to seven days. A rising cache hit ratio means the origin is doing less work.",
      },
      {
        target: "content-delivery-logs-tab",
        title: "Logs: each request, one line",
        description:
          "Every request the distribution answered: when, from which address, the path, the status sent, how much was sent and whether it came from the cache. Narrow by period, status or cache result, and download the page as a CSV file. The query string, cookies and the referrer are never kept.",
      },
    ],
  },
  // Video streaming (plan VID-7): source copy is docs/user/getting-started.md "Video streaming".
  "/video-streaming": {
    name: "Video streaming",
    steps: [
      {
        target: "video-table",
        title: "Your videos",
        description:
          "Each row is one video: upload a file and it is prepared for playback at several quality levels, so it plays smoothly on phones, computers and TVs whatever the viewer's connection. Open a video for its player, playback links and embed code.",
      },
      {
        target: "video-status",
        title: "Status tells you when it plays",
        description:
          "Waiting for upload means the file has not finished arriving. Processing means it is being prepared; the list refreshes itself. Ready means its playback links work. Failed shows the reason when you hover over it.",
      },
      {
        target: "video-views",
        title: "Views",
        description:
          "How many times each video was watched in the last 30 days, counted by the delivery network. A viewer who starts it again within half an hour counts once. The column appears once this cloud counts views.",
      },
      {
        target: "video-upload",
        title: "Upload a video",
        description:
          "Pick a file of up to 20 GB, or give a link to one or an object in one of your buckets, name it and choose who can watch it: public (anyone with the link) or signed (only with a playback token). An uploaded file goes straight to storage in parts; a part that fails is sent again, and you can retry or cancel. If the page closes, pick the same file again to send the rest. Once you pick a file, the dialog says what processing it and keeping it will cost.",
      },
      {
        target: "video-signing-keys-link",
        title: "Signing keys",
        description:
          "Signed videos play only with a playback token. Create a signing key here to sign tokens on your own server.",
      },
      {
        target: "video-settings-link",
        title: "Settings",
        description:
          "Where events about your videos go (a notification topic, your own webhook), which other sites' players may load your videos, and who can watch a new video by default.",
      },
    ],
  },
  "/video-streaming/settings": {
    name: "Video settings",
    steps: [
      {
        target: "video-settings-default",
        title: "Who can watch a new video",
        description:
          "Public or signed, for every new video that does not choose for itself. Each upload can still choose otherwise.",
      },
      {
        target: "video-settings-topic",
        title: "Events to a notification topic",
        description:
          "Each event — a video created, its file received, ready to play, failed or deleted — is published to this topic and from there to its subscribers.",
      },
      {
        target: "video-settings-webhook",
        title: "Events to your own webhook",
        description:
          "Each event is also sent to your web address as a signed request, oldest first. The secret is never shown again once saved: replace it to change it.",
      },
      {
        target: "video-settings-sites",
        title: "Allowed sites",
        description:
          "Sites whose pages may show your videos, with the embed code or their own player. Opening a video's player link directly always works.",
      },
    ],
  },
  "/video-streaming/signing-keys": {
    name: "Signing keys",
    steps: [
      {
        target: "video-signing-keys-table",
        title: "Keys that sign playback tokens",
        description:
          "Each row is a key your server uses to sign playback tokens for your signed videos. Last used says when a token signed with it last played a video.",
      },
      {
        target: "video-signing-keys-create",
        title: "Create a signing key",
        description:
          "The private key is shown once, to copy or download; it is not kept here. Deleting a key stops every token signed with it within a minute or two.",
      },
    ],
  },
  "/video-streaming/:id": {
    name: "Video",
    steps: [
      {
        target: "video-resume",
        title: "Resume upload",
        description:
          "An upload that stopped (the page closed, or the connection was lost) keeps every part already sent. Press Resume upload and pick the same file in the same browser: only the missing parts are sent. An upload not finished within a day is cancelled.",
      },
      {
        target: "video-player",
        title: "Watch it here",
        description:
          "The hosted player, as your viewers see it, with a quality menu and full screen. It appears when the video is ready.",
      },
      {
        target: "video-links",
        title: "Playback links",
        description:
          "Paste the HLS link into Safari, an iPhone or most TV and video players, the DASH link into most other players. Each has a copy button.",
      },
      {
        target: "video-embed",
        title: "Put it on your site",
        description:
          "Copy the frame code into any page, or the script code to control the player from your own page.",
      },
      {
        target: "video-token",
        title: "Get a playback token",
        description:
          "For a signed video: pick how long it works and get a token with the links already filled in, shown once. The embed code below then carries it too.",
      },
      {
        target: "video-policy",
        title: "Who can watch",
        description:
          "Public: anyone with the link. Signed: only with a playback token, and only until the token expires. Switching a ready video gives it new playback links when the change finishes; the old ones stop working (public links as soon as it starts becoming signed).",
      },
      {
        target: "video-captions-tab",
        title: "Captions",
        description:
          "Add a WebVTT or SRT file per language, up to 2 MB and 20 per video. Viewers pick them from the player's captions menu; other players find them in the HLS and DASH links. A file that cannot be used says why.",
      },
      {
        target: "video-analytics-tab",
        title: "How it plays for viewers",
        description:
          "Playbacks, how long the picture took to start, time spent waiting and the quality levels watched, as the hosted player measured them in your viewers' browsers.",
      },
      {
        target: "video-events-tab",
        title: "Events",
        description:
          "What happened to the video, newest first, and whether each event reached your webhook.",
      },
      {
        target: "video-tags-tab",
        title: "Tags",
        description: "Key/value labels for finding videos and splitting a bill by team or project.",
      },
    ],
  },
  "/functions/:name": {
    name: "Function",
    steps: [
      {
        target: "functions-invoke",
        title: "Call your function",
        description:
          "Send a request and see the response and timing straight away. Functions scale to zero when idle, so the first call after a quiet spell takes a little longer.",
      },
    ],
  },
  // PA-14: source copy is docs/user/getting-started.md "Protect a server you already run".
  "/compute/:id": {
    name: "Instance",
    steps: [
      {
        target: "resource-title",
        title: "One instance",
        description:
          "This page is one instance. Its state is beside its name, the buttons on the right act on it (start, stop, console, delete), and each tab below shows one side of it: what it is, how busy it is, what it is connected to and what happened to it.",
      },
      {
        target: "protect-private-access",
        title: "Reach it without a public IP",
        description:
          "Protect with Private access opens a new app for this server with its private address and its ports filled in. Your people reach it through the OmniCloud Access client, so it needs no public IP.",
      },
    ],
  },
  // PA-11: source copy is docs/user/getting-started.md "Private access: get started".
  "/access/get-started": {
    name: "Private access: get started",
    steps: [
      {
        target: "access-start-connector",
        title: "A connector carries the traffic",
        description:
          "A connector is a small program that sits next to your apps and carries your people's traffic to them. It only makes outbound connections, so nothing is opened to the internet. Give it a name.",
      },
      {
        target: "access-start-where",
        title: "Run it here or anywhere",
        description:
          "On this cloud, pick the network your apps are on and we start the connector for you. Elsewhere, copy the one-line command and run it on a machine in your data centre, another cloud or a Kubernetes cluster.",
      },
      {
        target: "access-start-status",
        title: "Watch it turn healthy",
        description:
          "This page follows the connector: Joining turns to Healthy in place once it reports in, usually within three minutes. Not reporting means it stopped checking in.",
      },
      {
        target: "access-start-app",
        title: "Add the app",
        description:
          "An app is the name or address your people use and its ports, as the connector sees it: wiki.corp.example on 443, say, or a server's private address on 22.",
      },
      {
        target: "access-start-policy",
        title: "You and your group, to start with",
        description:
          "An access policy says who may reach the app. We make one that lets in you and your first group; edit it to let more people in. Nobody else gets through until a policy allows them.",
      },
      {
        target: "access-start-test",
        title: "Try it",
        description:
          "For a web app, Open test link opens it through the client on this computer. For anything else, this card says how to reach it with the client running.",
      },
      {
        target: "access-start-client",
        title: "The client",
        description:
          "Your people reach private apps through the OmniCloud Access client: they sign in once and there is no connect button. Download it for this computer here.",
      },
    ],
  },
  // SASE (SASE-1). Copy shared with docs/user/getting-started.md "SASE".
  "/sase": {
    name: "SASE overview",
    steps: [
      {
        target: "sase-overview-checklist",
        title: "Set up SASE, step by step",
        description:
          "SASE puts your people's access to private apps and to the internet behind one sign-in and one set of policies. These steps tick themselves off as you go; a step marked Not available yet is a service this cloud does not offer yet.",
      },
      {
        target: "sase-overview-cards",
        title: "What is running",
        description:
          "A card for each service that is switched on here, with its live counts. Open one to manage it: it is the same service, with the same data, as in its own section.",
      },
    ],
  },
  "/points-of-presence": {
    name: "Points of presence",
    steps: [
      {
        target: "pops-tabs",
        title: "Locations and their nodes",
        description:
          "A location is a place the SASE service runs, with the addresses customers reach it on and the addresses their traffic leaves from. Its nodes do the work: every node serves every customer, so adding a node adds room for everyone.",
      },
      {
        target: "pops-tabs",
        title: "Drain before you retire",
        description:
          "Open a node to see its checks and what to do about each. Drain it first: it takes no new connections and its customers move to the other nodes. Only a drained node can be retired.",
      },
      {
        target: "pops-rollout",
        title: "Updates go to one node first",
        description:
          "A new configuration reaches one node first. Once it has run there healthy, the rest follow, one node per location at a time. If a node cannot take it, it keeps its previous configuration and the update stops, so nothing is ever half-applied.",
      },
    ],
  },
  // SASE → Cloud apps (SASE-20). Copy shared with docs/user/getting-started.md
  // "SASE" (Cloud apps).
  "/sase/cloud-apps": {
    name: "SASE: Cloud apps",
    steps: [
      {
        target: "sase-cloud-apps-tabs",
        title: "Cloud apps",
        description:
          "The software-as-a-service apps people use at work, how risky each one is and which ones your organisation approves. Overview sums up your decisions; Catalog lists every app.",
      },
      {
        target: "sase-cloud-apps-usage",
        title: "Use by your people",
        description:
          "Which apps are really in use, by whom and how much appears once security nodes carry your organisation's traffic. Until then this says so rather than show a made-up figure.",
      },
      {
        target: "sase-cloud-apps-decisions",
        title: "Your decisions",
        description:
          "How many apps you have sanctioned, left unsanctioned, put under watch or not reviewed yet. Select a number to see those apps in the catalog.",
      },
      {
        target: "sase-cloud-apps-catalog-info",
        title: "Where the catalog comes from",
        description:
          "Every attribute was found on the vendor's own public pages and links to where it was found. What was not found reads Not known and counts against the score, so an app that publishes nothing never looks safe.",
      },
      {
        target: "sase-cloud-apps-filters",
        title: "Find an app",
        description:
          "Search by name, vendor or address, or narrow the list by category, risk, your decision, a compliance claim or how much is known.",
      },
      {
        target: "sase-cloud-apps-table",
        title: "Risk from 0 to 100",
        description:
          "Higher is riskier. Open an app to see why it scored what it did, every attribute with its source, and to record your decision.",
      },
      {
        target: "sase-cloud-apps-score",
        title: "The score and how much is known",
        description:
          "The bar shows how much of the score rests on known facts rather than on attributes that are not known.",
      },
      {
        target: "sase-cloud-apps-decision",
        title: "Your decision",
        description:
          "Sanction, watch or leave an app unsanctioned, set your own score when your review disagrees with the catalog, and note why. Only your organisation sees these.",
      },
      {
        target: "sase-cloud-apps-why",
        title: "Why this score",
        description:
          "Each attribute adds its share: nothing when the answer is good, all of its weight when it is bad, and most of it when it is not known.",
      },
      {
        target: "sase-cloud-apps-attributes",
        title: "Every attribute and its source",
        description:
          "Security, compliance, data and operational facts, each linked to the vendor page it was found on. Not known means it was not found, not that the answer is no.",
      },
      {
        target: "sase-cloud-apps-discovered-source",
        title: "Apps your people really use",
        description:
          "Discovered lists the cloud apps found in your organisation's own web and DNS traffic over a time range, or in a log you uploaded. Pick where to read from here.",
      },
      {
        target: "sase-cloud-apps-upload",
        title: "Not using SASE yet? Upload a log",
        description:
          "Upload a traffic log from your own firewall or proxy (Palo Alto Networks, Fortinet, Cisco Umbrella, or a web proxy's access log) to see the same view. The file is read in the background; its report shows the apps once it is ready. People are counted, never named, and the report is kept apart from your traffic logs.",
      },
      {
        target: "sase-cloud-apps-discovered-table",
        title: "Sanction or unsanction in one click",
        description:
          "Each app with its risk, how many people used it and how much data went each way. Sanction or unsanction it here: it is the same decision the Catalog shows. An address that is not in the catalog has no decision; a URL list in Internet access blocks it.",
      },
      {
        target: "sase-saas-summary",
        title: "Your connected apps at a glance",
        description:
          "Connected apps reads your Microsoft 365 or Google account through its own APIs: files shared too widely, how many hold sensitive data, apps people gave access to, and settings that fail a check.",
      },
      {
        target: "sase-saas-connect",
        title: "Connect an account",
        description:
          "Register an app in your Microsoft 365 or Google admin console with the permissions listed, then enter its id and secret or key here. The secret is stored in the secret store and never shown again. Connecting needs the Premium tier.",
      },
      {
        target: "sase-saas-permissions",
        title: "What the app is allowed to do",
        description:
          "Each permission is listed with what it is used for: reading shared files and their sharing, removing a share, moving a file to quarantine, reading settings and listing the apps people granted.",
      },
      {
        target: "sase-saas-connections",
        title: "Scans and what they could not read",
        description:
          "Each connection is scanned on its schedule, reading only what changed since the last scan. If a permission is missing, the scan says which part was not read and carries on with the rest. Scan now starts one in the background.",
      },
      {
        target: "sase-saas-posture-scores",
        title: "A posture score per app",
        description:
          "Each connected app's settings are checked against published security baselines. The score is the share of checks that pass, weighted by how serious each one is. Re-check reads the settings again now.",
      },
      {
        target: "sase-saas-posture-table",
        title: "Every check, why it matters and the fix",
        description:
          "A failed check says what an attacker could do, how to fix it in the admin console and which baseline it comes from. A check reads Not checked when the app lacks the permission to read that setting.",
      },
      {
        target: "sase-saas-file-filters",
        title: "Files shared too widely",
        description:
          "Exposed files lists files shared with anyone who has the link or with people outside your organisation. Narrow it to files that hold sensitive data, to one app, or to dismissed and fixed files.",
      },
      {
        target: "sase-saas-files-table",
        title: "What Data protection found",
        description:
          "Each shared file is checked with your data profile: card numbers, social security numbers, cloud keys and more. Only the kind of data and a count are kept, never the matched text. Open a file to act on it.",
      },
      {
        target: "sase-saas-file-shares",
        title: "Choose the shares to change",
        description:
          "Every share of the file is listed; the ones that expose it can be chosen. With none chosen, an action applies to every share it covers. A share that comes from a parent folder is removed on the folder.",
      },
      {
        target: "sase-saas-file-actions",
        title: "Fix it, and tell the owner",
        description:
          "Turn off the link, remove the outside people, quarantine the file into the quarantine account your organisation chose, or just tell the owner. A quarantined file can be given back. Only administrators can do this; each change runs in the background and is listed under the file.",
      },
      {
        target: "sase-saas-app-filters",
        title: "Third-party and AI apps",
        description:
          "Third-party apps lists the apps people or an administrator gave access to your mail, files or directory, with AI assistants and agents marked. Narrow it to AI apps or to high-risk ones.",
      },
      {
        target: "sase-saas-apps-table",
        title: "What each app may reach, and its risk",
        description:
          "The risk score comes from what the app was granted: broad access to mail or files, access with no person signed in, consent for everyone, an unverified publisher, many people. Revoke removes every grant the scan found.",
      },
    ],
  },
  "/sase/data/exact": {
    name: "SASE: Exact data",
    steps: [
      {
        target: "sase-exact-data-columns",
        title: "Your own records, protected",
        description:
          "Exact data lets data rules recognise your organisation's own records, such as customer emails, card numbers or employee IDs, when someone sends them to a website or an AI app. Each column shows what it holds and how many different values are protected. The values themselves are never shown and never kept.",
      },
      {
        target: "sase-exact-data-upload",
        title: "Upload a CSV file",
        description:
          "Choose a CSV file, then choose which columns to protect, give each a name and say what it holds. Each value is turned into a one-way, keyed code as the file arrives, and the file itself is never stored. The new version is prepared in the background; this page shows when it is ready. A column named like one you already have keeps its place in your data rules.",
      },
      {
        target: "sase-exact-data-uploads",
        title: "Every upload",
        description:
          "Each upload with its rows, its protected values and the values skipped because they were shorter than four characters. The newest ready one is in use. To use exact data, open Policies, add a data profile and pick the columns it looks for.",
      },
    ],
  },
  "/sase/users": {
    name: "SASE: Users and devices",
    steps: [
      {
        target: "sase-device-checks-tab",
        title: "Device checks",
        description:
          "Connect CrowdStrike, SentinelOne, Microsoft Intune, Microsoft Defender or Jamf, and say what each must report about a device: compliant, a minimum score, or heard from recently. Every device is checked again every few minutes and whenever you change a check.",
      },
      {
        target: "sase-device-checks",
        title: "What a failing device gets",
        description:
          "Each check says what a failure does: report it, ask the person to sign in again, or disconnect the device. To keep a failing device out of your private apps, require a device that passes its health checks in their access policies.",
      },
    ],
  },
  "/sase/experience": {
    name: "SASE: Experience",
    steps: [
      {
        target: "sase-experience-tabs",
        title: "How your apps answer",
        description:
          "Your SASE locations test how quickly and reliably your apps answer. Overview scores each test, Route shows the way to an app hop by hop, and App tests chooses what is tested. Tests run from the locations only: there is no data from people's laptops, Wi-Fi or home networks yet.",
      },
      {
        target: "sase-experience-scores",
        title: "A score for every test",
        description:
          "Each test gets a score out of 100: availability times speed compared with the same test's usual median over the last 7 days, so twice as slow halves it. Open a test for each location, and see where the time went: DNS lookup, connection, secure connection and server response.",
      },
    ],
  },
  "/sase/data/fingerprints": {
    name: "SASE: Document fingerprints",
    steps: [
      {
        target: "sase-fingerprints-sets",
        title: "Your documents, recognised",
        description:
          "Document fingerprints let data rules recognise your own documents, such as board papers, contracts or designs, when someone sends a copy, a lightly edited copy or a pasted part of one to an AI app. Documents are grouped into sets; a rule names the sets it protects. Each document shows how it was read, its words and whether it is ready.",
      },
      {
        target: "sase-fingerprints-add-set",
        title: "Add a set",
        description:
          "Name a group of documents one rule protects, such as Board papers. Up to 16 sets.",
      },
      {
        target: "sase-fingerprints-upload",
        title: "Add a document",
        description:
          "Choose a Word, Excel, PowerPoint or plain text file. It is read as it arrives and only one-way, keyed fingerprints of short runs of its words are kept; the document itself is never stored. PDF files are not read yet: save them as Word or plain text first. The new version is prepared in the background, and this page shows when it is ready.",
      },
      {
        target: "sase-fingerprints-limits",
        title: "What is compared",
        description:
          "Security nodes compare prompts and uploaded files with your documents, each on its own; answers are not compared. To use a set, open Policies, add a data profile and pick the fingerprint sets it compares with: by default it acts on a prompt or file at least 50% like a whole document, or sharing 8 passages with one.",
      },
    ],
  },
  "/sase/get-started": {
    name: "SASE: get started",
    steps: [
      {
        target: "sase-start-steps",
        title: "Five steps",
        description:
          "Your progress, read from what exists: come back at any time and the page picks up where you left off.",
      },
      {
        target: "sase-start-org",
        title: "Your organisation",
        description:
          "The organisation holds your people, your apps and your policies. It is the one you use for Private access.",
      },
      {
        target: "sase-start-identity",
        title: "How your people sign in",
        description:
          "Connect the identity provider your company already uses, or turn on built-in sign-in for a trial. Every policy names people and groups from here.",
      },
      {
        target: "sase-start-connection",
        title: "Connect people and apps",
        description:
          "A connector next to your private apps carries your people's traffic to them. The ways to send internet traffic through SASE are listed here as they become available.",
      },
      {
        target: "sase-start-baseline",
        title: "A safe starting policy",
        description:
          "Once Internet access is available, one click puts a recommended set of rules in place. Every rule stays editable.",
      },
      {
        target: "sase-start-test",
        title: "Try it",
        description:
          "The policy tester says what would happen to a person, a device and an address, before anyone is affected.",
      },
    ],
  },
  // SASE-14: Analytics and Incidents. Copy shared with
  // docs/user/getting-started.md "SASE" (Analytics, Incidents).
  "/sase/analytics": {
    name: "SASE: Analytics",
    steps: [
      {
        target: "sase-analytics-tabs",
        title: "Overview and Activity search",
        description:
          "The overview shows the most frequent users, sites, categories, decisions and threats. Activity search lists every line behind them.",
      },
      {
        target: "sase-range",
        title: "The time range",
        description:
          "Every card and list on the page covers this range. It is part of the page's address, so a link you share shows the same view.",
      },
      {
        target: "sase-analytics-cards",
        title: "What stands out",
        description:
          "Each card lists the top five. Open a name to see that user's or site's own view, or a decision or category to search for it.",
      },
    ],
  },
  // SASE-52 (B18-F6, B21). Copy shared with docs/user/getting-started.md
  // "SASE" (Analytics, Reports).
  "/sase/analytics/reports": {
    name: "SASE: Reports",
    steps: [
      {
        target: "sase-reports-quantum-safe",
        title: "Quantum-safe encryption",
        description:
          "Of the connections the service inspected in the window, the share that used quantum-safe encryption on each side, and the devices and websites still on standard encryption. Use it to decide when Require in Settings would block nobody.",
      },
    ],
  },
  "/sase/analytics/activity": {
    name: "SASE: Activity search",
    steps: [
      {
        target: "sase-query-builder",
        title: "Build a search",
        description:
          "Type words to find, pick the kinds of traffic, and add exact filters one at a time: a user, an app or site, a category, a decision, a rule, a location. Remove a filter by its cross.",
      },
      {
        target: "sase-activity-table",
        title: "One line per request",
        description:
          "Each line is one web request, DNS lookup, firewall flow or threat alert, with the decision SASE took. Open a line to see every detail, and every other line of the same transaction.",
      },
      {
        target: "sase-saved-searches",
        title: "Save a search",
        description:
          "A saved search keeps its filters and its time range. It is kept in this browser for your account; to share one, send the page's link.",
      },
      {
        target: "sase-export",
        title: "Export",
        description: "Download the search as a spreadsheet file: up to 1000 lines, newest first.",
      },
    ],
  },
  "/sase/analytics/users/:user": {
    name: "SASE: user view",
    steps: [
      {
        target: "sase-subject-summary",
        title: "At a glance",
        description:
          "How many events this user had in the time range, how much data, what kinds, what SASE decided, and the sites they used most.",
      },
      {
        target: "sase-subject-timeline",
        title: "The timeline",
        description:
          "Everything the user did, newest first. Red is blocked or dropped, orange an alert or a warning, green allowed.",
      },
    ],
  },
  "/sase/analytics/apps/:app": {
    name: "SASE: app view",
    steps: [
      {
        target: "sase-subject-summary",
        title: "At a glance",
        description:
          "How much this app or site was used in the time range, what SASE decided, and who used it most.",
      },
      {
        target: "sase-subject-timeline",
        title: "The timeline",
        description: "Every use of the app or site, newest first, with the person behind it.",
      },
    ],
  },
  "/sase/incidents": {
    name: "SASE: Incidents",
    steps: [
      {
        target: "sase-incidents-summary",
        title: "One queue for everything",
        description:
          "Threats, malware, sensitive data, exact data, protected documents, hidden instructions in AI content, tool servers that are not approved and unusual activity all arrive here. Pick a source to see only its incidents.",
      },
      {
        target: "sase-incidents-queue",
        title: "Work the queue",
        description:
          "Each incident groups one kind of event about one person on one day. Open it to set its status, assign it, add notes, and open its records in Activity search. The incident keeps references only, never what a record contained.",
      },
      {
        target: "sase-incidents-tabs",
        title: "Threat activity",
        description:
          "Threat activity lists every threat alert in the time range, newest first, with the most frequent threats and the people they reached.",
      },
    ],
  },
  // SASE-7: one policy (plan §7.3 Policies). Source copy: docs/user/getting-started.md, SASE.
  "/sase/policies": {
    name: "SASE: Policies",
    steps: [
      {
        target: "sase-policies-start",
        title: "One policy for everything",
        description:
          "One policy holds every rule: websites, private apps, other traffic and name lookups. You write it as a draft, test it, and publish it when it is right.",
      },
      {
        target: "sase-policies-tabs",
        title: "Rule sets",
        description:
          "All rules shows every rule in the order traffic meets it. Internet, Firewall and DNS each hold their own rules, tried from the top; Private apps shows Private access's access policies in place.",
      },
      {
        target: "sase-policies-filters",
        title: "Find a rule",
        description:
          "Filter by rule set, action or tag, or search for a person, group, app or site. The filters stay in the address, so you can share the view.",
      },
      {
        target: "sase-policies-objects-add",
        title: "Objects",
        description:
          "Name a group of people, an office's addresses or a list of sites once, and use it in any rule. Each object shows the rules that use it.",
      },
      {
        target: "sase-policies-versions",
        title: "Versions",
        description:
          "Every publish is a version, with who published it and why. See what changed, compare two, or roll back: a rollback is a new publish of the old rules.",
      },
      {
        target: "sase-policies-change-control-tab",
        title: "Change control",
        description:
          "Optional: require a second person's approval for every publish. The person who asks never approves their own change; an owner can still publish at once in an emergency, with a reason the audit log highlights.",
      },
      {
        target: "sase-policies-tester-tab",
        title: "Test before anyone is affected",
        description:
          "Say who, on which device, from where, opens what: the tester answers with the rule that decides in each set and the final action. It changes nothing.",
      },
      {
        target: "sase-policies-tester",
        title: "What happens when…",
        description:
          "Say who is asking and where they are going; leave out anything else you do not know: an unknown fact never lets anyone in, and the answer names it.",
      },
      {
        target: "sase-policies-publish",
        title: "Publish",
        description:
          "Changes wait in the draft. Review and publish shows what will change and, if a rule cannot be enforced yet, says why before anything is published.",
      },
    ],
  },
  "/sase/policies/rules/:ruleId": {
    name: "SASE: a rule",
    steps: [
      {
        target: "sase-rule-sentence",
        title: "In plain words",
        description: "The rule as one sentence, updated as you change the form. Read it before you save.",
      },
      {
        target: "sase-rule-who",
        title: "Who",
        description:
          "Everyone, or some people: users, groups, or a people-and-groups object. Anyone listed under Except is left out.",
      },
      {
        target: "sase-rule-what",
        title: "What",
        description:
          "What the rule is about: sites, domains, lists and categories, or for Firewall rules, addresses, protocols and ports. Nothing set means everything.",
      },
      {
        target: "sase-rule-action",
        title: "Action",
        description:
          "What happens when the rule matches, and which profiles it carries. Put a rule in preview to keep it and test it without enforcing it.",
      },
    ],
  },
  // SASE-8: Internet access. Steps on a tab that is not open are skipped.
  "/sase/connections": {
    name: "SASE: Connections",
    steps: [
      {
        target: "connections-summary",
        title: "How traffic reaches SASE",
        description:
          "A connection is a way into your SASE service: a device's tunnel, a site's tunnels from its router, a browser pointed at the proxy, or DNS alone. These counts are what is connected now, and the locations it is served from.",
      },
      {
        target: "connections-tabs",
        title: "One tab per way in",
        description:
          "Client is for people's devices, Sites and tunnels for offices and data centres, Proxy for browsers set to use a proxy, and DNS for sending only lookups. Use any mix of them.",
      },
      {
        target: "connections-devices",
        title: "Devices",
        description:
          "Each row is one device with its own tunnel and address. Revoke a device and it is taken off every security node at their next update.",
      },
      {
        target: "connections-add-device",
        title: "Add a device",
        description:
          "Name the device and get its profile for the WireGuard app, as a file or a code to scan. The profile is shown once: its private key is not kept.",
      },
      {
        target: "connections-client-settings",
        title: "What goes through the tunnel",
        description:
          "Everything, or only the networks you list, minus the ones you keep off it, and how long a profile lasts. Every new profile carries these settings.",
      },
      {
        target: "connections-sites",
        title: "Sites",
        description:
          "A site is an office or a network connected with two tunnels from its router, to two security nodes. Open one for the configuration to paste into your router.",
      },
      {
        target: "connections-proxy",
        title: "The proxy",
        description:
          "Point browsers at the proxy address, or give them the PAC file address and the file decides what goes through the proxy and what goes direct.",
      },
      {
        target: "connections-dns",
        title: "DNS only",
        description:
          "Your own encrypted DNS names: set one as a device's secure DNS and its lookups go through your DNS protection policy.",
      },
    ],
  },
  "/sase/settings": {
    name: "SASE: Settings",
    steps: [
      {
        target: "sase-settings-org",
        title: "Your organisation",
        description:
          "Whether your traffic is served, your tier and modules, and the locations it may be served from. The first save sets SASE up and gives the organisation its own device addresses.",
      },
      {
        target: "sase-settings-certificate",
        title: "The inspection certificate",
        description:
          "Your devices trust this certificate so encrypted sites can be inspected. Download it for your devices; its private key never leaves the service. Replace it if you ever need to, and keep the old one while devices move over.",
      },
      // SASE-52 (B18-F6, B21). Copy shared with docs/user/getting-started.md
      // "SASE" (Settings).
      {
        target: "sase-settings-quantum-safe",
        title: "Quantum-safe encryption",
        description:
          "Keep Prefer unless a device or a network in the way fails with it: encryption is then quantum-safe wherever both ends support it, and standard elsewhere. Require blocks devices and websites that cannot use it; Off is for compatibility only. You can set each side apart.",
      },
      // SASE-35 (P35c). Copy shared with docs/user/getting-started.md "SASE" (Partners).
      {
        target: "sase-partner-access",
        title: "Partner access",
        description:
          "If a provider manages your SASE for you, accept the invitation code they gave you and choose what they may do. You see what they did, in your own activity under their name, and you can remove them at any time.",
      },
    ],
  },
  // SASE-35 (P35c): a partner's page. Copy shared with docs/user/getting-started.md
  // "SASE" (Partners).
  "/sase/settings/partner": {
    name: "SASE: Partner",
    steps: [
      {
        target: "partner-customers",
        title: "Your customers",
        description:
          "Every organisation you manage, with its health, the threats found and stopped in the last 7 days, and what it used this month. Only organisations that accepted your invitation are here.",
      },
      {
        target: "partner-manage",
        title: "Manage a customer",
        description:
          "Opens the customer's SASE pages as that customer, as far as the customer allows: change settings or view only. A banner says whose pages you are on, and everything you do is recorded in the customer's activity under your name.",
      },
      {
        target: "partner-invite",
        title: "Invite a customer",
        description:
          "Make a single-use code and give it to the customer's administrator. They enter it in SASE Settings, under Partner access, and may allow you less than you ask.",
      },
      {
        target: "partner-branding",
        title: "Your branding",
        description:
          "Your name and block page text appear on your customers' block pages unless a customer wrote its own. Your logo, colours, help contact and app name are shown to your customers in the console.",
      },
    ],
  },
  "/sase/internet": {
    name: "SASE: Internet access",
    steps: [
      {
        target: "internet-tabs",
        title: "Your people and the web",
        description:
          "Internet access decides which websites your people may use, what is inspected on the way, and what they see when a site is stopped. Each tab is one part of it.",
      },
      {
        target: "internet-overview",
        title: "What is set",
        description:
          "How many categories you block, your URL lists, and whether decryption is on. Traffic figures join here once the security nodes carry your traffic.",
      },
      {
        target: "internet-publish",
        title: "Publish to apply",
        description:
          "Changes on these tabs are saved as a draft of your policy. Publishing sends the whole policy to the security nodes as one new version you can roll back.",
      },
      {
        target: "internet-feeds",
        title: "Where categories come from",
        description:
          "Each source of category data is listed with its version, age and licence. Open sources are for demonstrations only.",
      },
      {
        target: "internet-categories",
        title: "Allow or stop whole categories",
        description:
          "Pick an action for each category, such as blocking gambling. The page writes the matching rule in your policy; an action the security nodes cannot take yet is kept in preview.",
      },
      {
        target: "internet-url-lists",
        title: "Your own lists of sites",
        description:
          "A URL list names sites exactly (example.com) or with everything below them (*.example.com). Entries can expire, and a CSV file can fill a list.",
      },
      {
        target: "internet-decryption",
        title: "Decryption",
        description:
          "Decryption lets the security nodes look inside encrypted pages. It is off by default, banking, health and government sites are never opened, and apps that check their own certificate are left alone.",
      },
      {
        target: "internet-tenant-controls",
        title: "Only your company's accounts",
        description:
          "Stop people signing in to personal Microsoft 365, Google, Slack or Dropbox accounts from your devices. This needs decryption, and the page says when it is not applied.",
      },
      {
        target: "internet-content-controls",
        title: "Safe search, YouTube and files",
        description:
          "Safe search is applied by DNS protection. File-type, size and bandwidth limits are saved and marked as not enforced until the security nodes can apply them.",
      },
      {
        target: "internet-pages",
        title: "What people see",
        description:
          "Write the block, warn, coach and caution pages in your own words, with a preview beside the form.",
      },
      {
        target: "internet-lookup",
        title: "Look up a site",
        description:
          "Type an address to see its category, where that answer comes from and on what licence, and ask for a different category if it is wrong.",
      },
    ],
  },
  // SASE-9/10/11. Copy shared with docs/user/getting-started.md "SASE".
  "/sase/dns": {
    name: "SASE: DNS protection",
    steps: [
      {
        target: "sase-dns-tabs",
        title: "Protection at the first step",
        description:
          "Every name your people look up is checked before it is answered: threats and the categories you choose are blocked there, before any connection is made.",
      },
      {
        target: "sase-publish-bar",
        title: "Draft, then publish",
        description:
          "Changes go to a draft of your organisation's one policy. Publish makes the draft the next version; each version can be compared and rolled back under Policies.",
      },
      {
        target: "sase-dns-policy",
        title: "What to block",
        description:
          "Threat lists, content categories, newly registered domains, encrypted DNS that would skip this protection, safe search for the big search engines, and threat detection: made-up domain names, data hidden in lookups and newly seen domains.",
      },
      {
        target: "sase-dns-lists",
        title: "Your own lists",
        description:
          "Names you always allow and names you always block. An allowed name is answered even when a list or a category would block it.",
      },
      {
        target: "sase-dns-split",
        title: "Internal domains",
        description:
          "Names such as intranet.corp.example are answered by your own DNS servers instead of the internet.",
      },
    ],
  },
  "/sase/firewall": {
    name: "SASE: Firewall",
    steps: [
      {
        target: "sase-firewall-rules",
        title: "Rules, in order",
        description:
          "Each rule allows or blocks connections by address, protocol and port. The first rule that matches decides; the default at the bottom decides the rest.",
      },
      {
        target: "sase-publish-bar",
        title: "Draft, then publish",
        description:
          "Rules are saved to a draft of your organisation's policy. Publish makes them the next version.",
      },
      {
        target: "sase-firewall-ips",
        title: "Intrusion prevention",
        description:
          "Connections this firewall allows are also inspected for known attacks. The profile is chosen under Threat protection.",
      },
    ],
  },
  "/sase/threats": {
    name: "SASE: Threat protection",
    steps: [
      {
        target: "sase-threats-tabs",
        title: "Threat protection",
        description:
          "Intrusion prevention for traffic other than web browsing, antivirus for files, your own indicators, and the threat feeds the service uses.",
      },
      {
        target: "sase-threats-ips-profile",
        title: "How strict",
        description:
          "Strict blocks every known attack, Balanced blocks the serious ones, Alert only blocks nothing and logs every match.",
      },
      {
        target: "sase-threats-signatures",
        title: "Your own signatures",
        description:
          "Add a signature for something the built-in ones do not know. It applies to your organisation's traffic only.",
      },
      {
        target: "sase-threats-indicators",
        title: "Indicators",
        description:
          "Paste addresses, domains, URLs or file hashes from a threat report to block them, with an expiry date if you want one.",
      },
      {
        target: "sase-threats-feeds",
        title: "Where the threat lists come from",
        description: "Every feed with its source, its licence and when it was last updated.",
      },
    ],
  },
  // SASE → AI security (SASE-13). Copy shared with docs/user/getting-started.md
  // "SASE" (AI security).
  "/sase/ai": {
    name: "SASE: AI security",
    steps: [
      {
        target: "sase-ai-tabs",
        title: "AI security",
        description:
          "Which AI apps your people may use, what must never be typed into them, and the gateways in front of your own AI agents, each on its own tab.",
      },
      {
        target: "sase-ai-usage",
        title: "AI use",
        description:
          "Once security nodes carry your organisation's traffic, the AI apps in use, who uses them and the prompts blocked appear here. Until then it says so rather than show a made-up figure.",
      },
      {
        target: "sase-ai-overview-apps",
        title: "What you have set",
        description:
          "How many AI apps you allow, block, keep to your company's account or make read only. Select a number to see those apps.",
      },
      {
        target: "sase-ai-overview-prompts",
        title: "Prompt protection",
        description:
          "Your rules for what must not be typed or uploaded into AI apps, and whether they are applied yet.",
      },
    ],
  },
  "/sase/ai/apps": {
    name: "SASE: AI apps",
    steps: [
      {
        target: "sase-ai-apps-filters",
        title: "Find an app",
        description:
          "Search by name, vendor or address, or narrow the list by category, risk assessment, confidence or your own setting. The filters stay in the address, so you can share the view.",
      },
      {
        target: "sase-ai-apps-table",
        title: "Your setting on each app",
        description:
          "Allow, block, company account only or read only. Open an app for its addresses, its risk details and where each one comes from.",
      },
      {
        target: "sase-ai-corporate",
        title: "Company accounts",
        description:
          "Company account only needs to know your company's own accounts of ChatGPT, Claude, Copilot or Gemini. Without them, that setting blocks the app. For Gemini it restricts every Google service, Gmail and Drive included.",
      },
    ],
  },
  "/sase/ai/prompt-protection": {
    name: "SASE: Prompt protection",
    steps: [
      {
        target: "sase-ai-prompts-status",
        title: "Not applied yet",
        description:
          "You can prepare and save your rules now. Nothing is blocked or logged until security nodes carry your organisation's traffic.",
      },
      {
        target: "sase-ai-prompts-sets",
        title: "What we look for",
        description:
          "Card numbers, social security numbers, phone numbers, email addresses, cloud keys and sign-in tokens, each checked so that look-alikes are not counted.",
      },
      {
        target: "sase-ai-prompts-rules",
        title: "Rules",
        description:
          "A rule looks for one or more of those, or your own patterns, in what people send, in the app's answers or in both, and either blocks with your coaching message or allows and logs it.",
      },
      {
        target: "sase-ai-prompts-fallbacks",
        title: "When a prompt cannot be read",
        description:
          "Automatic blocks a prompt that cannot be read while a rule blocks or masks what people send, and allows and logs it otherwise. Choose Allow or Block instead for every app, or for one app under App by app.",
      },
      {
        target: "sase-ai-prompts-coach",
        title: "Coaching message",
        description:
          "What people see when a prompt is blocked: say why and where to go instead, such as your company's own AI assistant.",
      },
    ],
  },
  "/sase/ai/conversations": {
    name: "SASE: Conversations and injection",
    steps: [
      {
        target: "sase-ai-checks-status",
        title: "Not applied yet",
        description:
          "You can prepare these checks now. Nothing is blocked or logged until security nodes carry your organisation's traffic.",
      },
      {
        target: "sase-ai-checks-conversation",
        title: "Prompts checked together",
        description:
          "A card number or key split over two prompts is still found by your Prompt protection rules. It is blocked, never masked: the first half was already sent.",
      },
      {
        target: "sase-ai-checks-injection",
        title: "Hidden instructions",
        description:
          "An alert when an AI answer, or what an MCP server sends back to an agent, carries instructions meant to steer the AI. Alerts first: an answer that merely discusses the subject can be flagged.",
      },
    ],
  },
  "/sase/ai/mcp-servers": {
    name: "SASE: MCP servers",
    steps: [
      {
        target: "sase-ai-mcp-status",
        title: "Not applied yet",
        description:
          "The settings are saved but not applied until security nodes carry your organisation's traffic. Servers seen earlier are listed from your logs.",
      },
      {
        target: "sase-ai-mcp-settings",
        title: "Finding MCP servers",
        description:
          "MCP servers are the tools AI agents call. Turn this on to record which server and tool each request used, and allow, alert on or block servers you have not approved.",
      },
      {
        target: "sase-ai-mcp-approved",
        title: "Approved automatically",
        description:
          "Your agent gateways and the MCP tool servers behind them are approved without you listing them. Add others by host name below.",
      },
      {
        target: "sase-ai-mcp-seen",
        title: "Seen in your traffic",
        description:
          "Every MCP server found in your logs, with whether today's list approves it. For one that is not approved, put it behind an agent gateway: its form opens filled in, and nothing is created until you save it.",
      },
    ],
  },
  "/identity": {
    name: "Users & access",
    steps: [
      {
        target: "identity-tabs",
        title: "Who can sign in, and to what",
        description:
          "Users belong to projects, and roles say what they can do there. This admin area manages all three across the platform.",
      },
      {
        target: "identity-create-user",
        title: "Add a user",
        description:
          "Create an account and assign it to a project with a role. The user signs in with those credentials and sees only that project's resources.",
      },
    ],
  },
};
